import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Plus, Eye, Edit, Trash2, Building2, User, AlertTriangle, Shield, CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { $api } from '../../api';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useQueryClient } from '@tanstack/react-query';
import AddEntityModal from './AddEntityModal';
import DeleteConfirmModal from './DeleteConfirmModal';
import ErrorMessage from '../ErrorMessage';

interface Entity {
  id: string;
  name: string;
  kind: 'company' | 'person';
  email?: string;
  risk_level: 'low' | 'mid' | 'high';
  alerts_pending: number;
  created_at: string;
  updated_at: string;
}

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 10;

const EntitiesList: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams<{ organizationId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentOrganization } = useOrganization();
  const organizationId = params.organizationId || currentOrganization?.id || '';
  
  // Get pagination params from URL or use defaults
  const page = parseInt(searchParams.get('page') || '1', 10);
  const pageSize = parseInt(searchParams.get('pageSize') || String(DEFAULT_PAGE_SIZE), 10);
  const activeTabFromUrl = searchParams.get('kind') as 'company' | 'person' | null;
  
  const [activeTab, setActiveTab] = useState<'company' | 'person'>(activeTabFromUrl || 'company');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingEntity, setEditingEntity] = useState<Entity | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [entityToDelete, setEntityToDelete] = useState<Entity | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Sync activeTab with URL param
  useEffect(() => {
    if (activeTabFromUrl && activeTabFromUrl !== activeTab) {
      setActiveTab(activeTabFromUrl);
      // Reset to page 1 when changing tab
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.set('kind', activeTabFromUrl);
        newParams.set('page', '1');
        return newParams;
      });
    }
  }, [activeTabFromUrl, activeTab, setSearchParams]);

  // Fetch entities from API with pagination
  const entitiesQuery = $api.useQuery(
    'get',
    '/api/organization/{organization_id}/entities',
    {
      queryKey: ['entities', organizationId, activeTab, page, pageSize],
      params: {
        path: { organization_id: organizationId },
        query: {
          kind: activeTab,
          page: String(page),
          limit: String(pageSize),
        },
      },
    },
    {
      enabled: !!organizationId,
      refetchOnMount: true, // Always refetch when component mounts if query is stale
      staleTime: 0, // Consider data stale immediately so it refetches after invalidation
    }
  );

  const entities = entitiesQuery.data?.entities || [];
  const pagination = entitiesQuery.data?.pagination || {
    page: 1,
    limit: pageSize,
    total: 0,
    pages: 1,
  };

  const getRiskLabel = (riskLevel: 'low' | 'mid' | 'high') => {
    switch (riskLevel) {
      case 'high':
        return { label: 'Risque élevé', color: 'bg-red-100 text-red-800', icon: AlertTriangle };
      case 'mid':
        return { label: 'Risque moyen', color: 'bg-yellow-100 text-yellow-800', icon: Shield };
      case 'low':
      default:
        return { label: 'Risque faible', color: 'bg-green-100 text-green-800', icon: CheckCircle };
    }
  };

  const getActionRequired = (entity: Entity) => {
    if (entity.alerts_pending > 0) {
      return { required: true, label: `${entity.alerts_pending} alerte(s) en attente`, color: 'bg-red-100 text-red-800' };
    } else {
      return { required: false, label: 'Aucune action', color: 'bg-green-100 text-green-800' };
    }
  };

  // Update URL when pagination changes
  const updateUrlParams = (updates: { page?: number; pageSize?: number; kind?: 'company' | 'person' }) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (updates.page !== undefined) {
        newParams.set('page', String(updates.page));
      }
      if (updates.pageSize !== undefined) {
        newParams.set('pageSize', String(updates.pageSize));
        newParams.set('page', '1'); // Reset to page 1 when changing page size
      }
      if (updates.kind !== undefined) {
        newParams.set('kind', updates.kind);
        newParams.set('page', '1'); // Reset to page 1 when changing tab
      }
      return newParams;
    });
  };

  const handlePageChange = (newPage: number) => {
    updateUrlParams({ page: newPage });
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageSizeChange = (newPageSize: number) => {
    updateUrlParams({ pageSize: newPageSize });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleTabChange = (tab: 'company' | 'person') => {
    updateUrlParams({ kind: tab });
  };

  const handleAddClick = () => {
    setIsModalOpen(true);
  };

  const handleModalSubmit = () => {
    // Invalidate and refetch entities after successful add/edit
    queryClient.invalidateQueries({ 
      queryKey: ['entities', organizationId],
      refetchType: 'active'
    });
    entitiesQuery.refetch();
    setSuccess('Entité enregistrée avec succès');
    setTimeout(() => setSuccess(null), 5000);
    if (isEditMode && editingEntity) {
      setIsEditMode(false);
      setEditingEntity(null);
    }
  };

  const handleEditClick = (entity: Entity) => {
    setEditingEntity(entity);
    setIsEditMode(true);
    setIsModalOpen(true);
  };

  const handleViewClick = (entity: Entity) => {
    navigate(`/${organizationId}/entity/${entity.id}`);
  };

  const handleDeleteClick = (entity: Entity) => {
    setEntityToDelete(entity);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = () => {
    // Invalidate and refetch entities list after successful deletion
    queryClient.invalidateQueries({ 
      queryKey: ['entities', organizationId],
      refetchType: 'active'
    });
    entitiesQuery.refetch();
    setSuccess('Entité supprimée avec succès');
    setTimeout(() => setSuccess(null), 5000);
    setEntityToDelete(null);
    setIsDeleteModalOpen(false);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setIsEditMode(false);
    setEditingEntity(null);
  };

  if (!organizationId) {
    return (
      <div className="p-6">
        <div className="text-center text-gray-500 py-12">
          <p>Aucune organisation sélectionnée</p>
        </div>
      </div>
    );
  }

  if (entitiesQuery.isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Chargement des entités...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Gestion des Entités</h2>
          <p className="text-gray-600 mt-1">
            {pagination.total} entité{pagination.total !== 1 ? 's' : ''} au total
          </p>
        </div>
      </div>

      {/* Success Message */}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-green-800 text-sm">{success}</p>
        </div>
      )}

      {/* Error Message */}
      {entitiesQuery.error && (
        <ErrorMessage error={entitiesQuery.error} className="mb-4" />
      )}

      {/* Tabs and Controls */}
      <div className="flex space-x-1 mb-6">
        <button
          onClick={() => handleTabChange('company')}
          className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'company'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <Building2 className="h-4 w-4" />
          <span>Entreprises</span>
        </button>
        <button
          onClick={() => handleTabChange('person')}
          className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'person'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <User className="h-4 w-4" />
          <span>Personnes physiques</span>
        </button>
        <div className="flex-1"></div>
        <button
          onClick={handleAddClick}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
        >
          <Plus className="h-4 w-4" />
          <span>
            {activeTab === 'company' ? 'Nouvelle Entreprise' : 'Nouvelle Personne'}
          </span>
        </button>
      </div>

      {/* Entity Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {entities.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Building2 className="h-12 w-12 mx-auto mb-3 text-gray-400" />
            <p>Aucune {activeTab === 'company' ? 'entreprise' : 'personne'} trouvée</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nom
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Risque
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Alertes
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date d'ajout
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {entities.map((entity) => {
                const riskInfo = getRiskLabel(entity.risk_level);
                const actionInfo = getActionRequired(entity);
                const RiskIcon = riskInfo.icon;
                
                return (
                <tr key={entity.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <button 
                       onClick={() => handleViewClick(entity)}
                        className="text-sm font-medium text-gray-900 hover:text-blue-600 transition-colors cursor-pointer text-left"
                      >
                        {entity.name}
                      </button>
                      {entity.email && (
                        <div className="text-sm text-gray-500">{entity.email}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex items-center space-x-1 text-xs leading-5 font-semibold rounded-full ${riskInfo.color}`}>
                      <RiskIcon className="h-3 w-3" />
                      <span>{riskInfo.label}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${actionInfo.color}`}>
                      {actionInfo.label}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(entity.created_at).toLocaleDateString('fr-FR')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                    <button
                      onClick={() => handleViewClick(entity)}
                      className="text-blue-600 hover:text-blue-900 transition-colors p-1"
                      title="Voir les détails"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button 
                      onClick={() => handleEditClick(entity)}
                      className="text-gray-600 hover:text-gray-900 transition-colors p-1"
                      title="Modifier"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button 
                      onClick={() => handleDeleteClick(entity)}
                      className="text-red-600 hover:text-red-900 transition-colors p-1"
                      title="Supprimer"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination Controls */}
      {(pagination.pages > 1 || pagination.total > 5) && (
        <div className="mt-6 flex items-center justify-between">
          {/* Page Size Selector - shown on the left */}
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600">Par page:</label>
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(parseInt(e.target.value, 10))}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            >
              {PAGE_SIZE_OPTIONS.map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>

          {pagination.pages > 1 && (
            <div className="flex items-center space-x-2">
              <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page <= 1}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
            >
              <ChevronLeft className="h-4 w-4" />
              <span>Précédent</span>
            </button>
            
            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(pagination.pages, 7) }, (_, i) => {
                let pageNum: number;
                if (pagination.pages <= 7) {
                  pageNum = i + 1;
                } else if (pagination.page <= 4) {
                  pageNum = i + 1;
                } else if (pagination.page >= pagination.pages - 3) {
                  pageNum = pagination.pages - 6 + i;
                } else {
                  pageNum = pagination.page - 3 + i;
                }

                if (pagination.pages > 7 && i === 0 && pagination.page > 4) {
                  return (
                    <React.Fragment key={`start-${i}`}>
                      <button
                        onClick={() => handlePageChange(1)}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                      >
                        1
                      </button>
                      <span className="px-2 text-gray-500">...</span>
                    </React.Fragment>
                  );
                }

                if (pagination.pages > 7 && i === 6 && pagination.page < pagination.pages - 3) {
                  return (
                    <React.Fragment key={`end-${i}`}>
                      <span className="px-2 text-gray-500">...</span>
                      <button
                        onClick={() => handlePageChange(pagination.pages)}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                      >
                        {pagination.pages}
                      </button>
                    </React.Fragment>
                  );
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-3 py-2 border rounded-lg text-sm font-medium ${
                      pageNum === pagination.page
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page >= pagination.pages}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
            >
              <span>Suivant</span>
              <ChevronRight className="h-4 w-4" />
            </button>
            </div>
          )}

          {pagination.pages > 1 && (
            <div className="text-sm text-gray-600">
              Page {pagination.page} sur {pagination.pages}
            </div>
          )}
        </div>
      )}

      {/* Add Entity Modal */}
      <AddEntityModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        entityType={activeTab === 'company' ? 'entreprise' : 'personne'}
        onSubmit={handleModalSubmit}
        editingEntity={editingEntity}
        isEditMode={isEditMode}
        organizationId={organizationId}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setEntityToDelete(null);
        }}
        entityName={entityToDelete?.name || ''}
        onConfirm={handleDeleteConfirm}
        organizationId={organizationId}
        entityId={entityToDelete?.id || ''}
      />
    </div>
  );
};

export default EntitiesList;